import React from 'react';
import PointSceneManager from './PointSceneManager';
import PointEvents from '../Enumerations/ViewerEnums';
import FacilityMiniMap from '../../components/FacilityMiniMap';
import FacilityStore from '../../stores/FacilityStore';
import ViewerStore from '../../stores/ViewerStore';
import * as Inovx from '@shared';
import ViewerActions from '../../actions/ViewerActions';
import PointViewer from './PointViewer';
import FacilityActions from '../../actions/FacilityActions';
import AnnotationTextEditor from '../components/AnnotationTextEditor';
import AnnotationLinkEditor from '../components/AnnotationLinkEditor';
import { SpeedDialAnimationSettingsModel, SpeedDialComponent, SpeedDialItemEventArgs, SpeedDialItemModel, FabComponent } from '@syncfusion/ej2-react-buttons';
import AnnotationEditor from '../components/AnnotationEditor';
import KnowledgeBookActions from '../../actions/KnowledgeBookActions';
import KnowledgeBookStore from '../../stores/KnowledgeBookStore';
import ComponentHelpers from '../../helpers/ComponentHelpers';
import GraphicSourceActions from '../../actions/GraphicSourceActions';
import ViewerHelpers from '../../helpers/ViewerHelpers';
import FacilityAssetSetStore from '../../stores/FacilityAssetSetStore';
import { mergeViewerAssetSets } from '../../helpers/viewerAssetSetModel';
const { useState, useEffect, useRef, useMemo } = React;

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var SystemActions = Inovx.Shared.System.Actions;

interface PointViewerProps {
    displayMiniMap: boolean;
}

interface IPointViewInfo {
    view: IView360Detail;
    kvID?: string;
    kvTags?: IViewAnnotationTag[];
    kvVersion?: number;
    cameraProperties?: IPanoCameraProperties;
    cameraDirection?: IVector3D;
}

interface IKVTagsInfo {
    tags: IViewAnnotationTag[];
    version: number;
}

const getPanoViewInfo = (): IPointViewInfo => {
    const kv = ViewerStore.getSelectedKnowledgeView();
    return {
        view: ViewerStore.getSelectedViewImage(),
        kvID: kv?.ID,
        kvTags: kv?.Tags,
        kvVersion: ViewerStore.getKVContentVersion(),
        cameraProperties: (kv) ? { alpha: kv.DisplayProperties?.Yaw, beta: kv.DisplayProperties?.Pitch, fov: kv.DisplayProperties?.Fov } : null,
        cameraDirection: ViewerStore.getFocusedAnnotation()?.Target3D
    }
}

const getKVTagsInfo = (): IKVTagsInfo => {
    return {
        tags: ViewerStore.getSelectedKnowledgeView()?.Tags,
        version: ViewerStore.getTagsVersion()
    }
}

const getKVAssetSetsSignature = (): string => {
    const sets = ViewerStore.getSelectedKnowledgeView()?.AssetSets ?? [];
    return sets
        .map(set => `${set.ID}:${set.Ord}:${set.Color}:${set.Hidden ? 1 : 0}`)
        .sort()
        .join('|');
};

const getViewRenderSignature = (view?: IView360Detail | null): string => {
    if (!view) {
        return '';
    }

    const position = view.Position;
    const rotation = view.Rotation;

    return [
        view.ID,
        position?.X,
        position?.Y,
        position?.Z,
        rotation?.X,
        rotation?.Y,
        rotation?.Z,
        rotation?.W,
        view.ImageUrl,
        view.DepthMapUrl,
        view.Width,
        view.Height,
        view.Radius,
        view.ScaleFactor,
        view.PanoColorType
    ].join('|');
}

const ReactPointViewer = React.forwardRef((props: PointViewerProps, ref) => {
    const pointSceneManagerRef = useRef<PointSceneManager>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Facility store data
    const [facilityTitle] = useState<string>(FacilityStore.getSelectedFacility().Name);
    const [siteMap, setSiteMap] = useState<ISiteMap>(getSiteMap());
    const [views, setViews] = useState<IView360Detail[]>(FacilityStore.getSelectedFacility().Views);
    const [assetTags, setAssetTags] = useState<IAssetTag[]>(FacilityStore.getSelectedFacility().AssetTags);
    const [assemblies, setAssemblies] = useState<IAssembly[]>(ViewerStore.getAssemblies());
    const [assemblyTemplates, setAssemblyTemplates] = useState<IAssemblyTemplate[]>(FacilityStore.getSelectedFacility().AssemblyTemplates);
    const [graphicSources, setGraphicSources] = useState<IGraphicSource[]>(FacilityStore.getGraphicSources());
    const [selectedAssembly, setSelectedAssembly] = useState<string>(ViewerStore.getSelectedAssembly());
    // Viewer store data
    const [openPointCloud, isOpenPointCloud] = useState<boolean>(ViewerStore.isOpenPointCloud());
    const [measurements, setMeasurements] = useState<Measurement[]>(ViewerStore.getMeasurements());
    const [displayCompass, setDisplayCompass] = useState<boolean>(ViewerStore.getSettings().PanoCompassVisible);
    const [panoScanSphereColor, setPanoScanSphereColor] = useState<string | undefined>(ViewerStore.getSettings().PanoScanSphereColor);
    const [panoImageSphereColor, setPanoImageSphereColor] = useState<string | undefined>(ViewerStore.getSettings().PanoImageSphereColor);
    const [tagVolumeBoxColor, setTagVolumeBoxColor] = useState<string | undefined>(ViewerStore.getSettings().TagVolumeBoxColor);
    const [measurementColor, setMeasurementColor] = useState<string>(ViewerStore.getSettings().MeasurementColor);
    const [selectedView, setSelectedView] = useState<IPointViewInfo>(getPanoViewInfo());
    const [selectedAnnotation, setSelectedAnnotation] = useState<string>(ViewerStore.getSelectedAnnotation());
    const [displayObstructedTags, setDisplayObstructedTags] = useState<boolean>(ViewerStore.getDisplayObstructedTags());
    const [focusedAnnotation, setFocusedAnnotation] = useState<IAnnotationDisplayItem>(ViewerStore.getFocusedAnnotation());
    const [focusedMeasurement, setFocusedMeasurement] = useState<Measurement>(ViewerStore.getFocusedMeasurement());
    const [occludedAnnotations, setOccludedAnnotations] = useState<string[]>(ViewerStore.getOccludedAnnotations());
    const [filteredAssets, setFilteredAssets] = useState<string[]>(ViewerStore.getFilteredAssets());
    const [visibleAssetTagClasses, setVisibleAssetTagClasses] = useState<string[] | null>(ViewerStore.getVisibleAssetTagClasses());
    const [depthMapLoaded, setDepthMapLoaded] = useState<boolean>(ViewerStore.isDepthMapLoaded());
    const [displayMode, setDisplayMode] = useState<DisplayMode>(ViewerStore.getDisplayMode());
    const [measurementType, setMeasurementType] = useState<MeasurementType>(ViewerStore.getMeasurementType());
    const [assemblyCreationID, setAssemblyCreationID] = useState<string>(ViewerStore.getAssemblyCreationID());
    const [assemblyCreationTemplate, setAssemblyCreationTemplate] = useState<IAssemblyTemplate>(ViewerStore.getAssemblyCreationTemplate());
    const [kvTagsInfo, setKvTagsInfo] = useState<IKVTagsInfo>(getKVTagsInfo());
    const [kvAssetSetsSignature, setKvAssetSetsSignature] = useState<string>(getKVAssetSetsSignature());
    // Bumped when the Facility-wide Asset Sets are (re)loaded, so annotations re-resolve
    // once the Knowledge View tag fallback becomes available.
    const [facilityAssetSetsRevision, setFacilityAssetSetsRevision] = useState(0);
    const [kvName, setKvName] = useState<string>(ViewerStore.getSelectedKnowledgeView()?.Name);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isAnnotationTextShow, setIsAnnotationTextShow] = useState(false);
    const [isAnnotationDetailShow, setIsAnnotationDetailShow] = useState(false);
    const [isAnnotationLinkDetailShow, setIsAnnotationLinkDetailShow] = useState(false);
    const [lastAnnotationPosition, setLastAnnotationPosition] = useState({ x: 0, y: 0 });
    const [speedDialButtonTitle, setSpeedDialButtonTitle] = useState<string>(InovxLoader.t("facility:action.editAnnotation"));

    const [directionDegrees, setDirectionDegrees] = useState(0);
    const [activeView, setActiveView] = useState<IView360Detail>(null);
    const [highlightedView, setHighlightedView] = useState<IView360Detail>(null);
    const [shouldSpeedDialClose, setShouldSpeedDialClose] = useState(false);

    const isSceneLoading = useRef<boolean>(true);
    const isSceneDirty = useRef<boolean>(false);
    const isSceneEditable = useRef<boolean>(false);

    //ref for annotation popup
    const isAnnotationTextShowRef = useRef(isAnnotationTextShow);
    const isAnnotationDetailShowRef = useRef(isAnnotationDetailShow);
    const isAnnotationLinkDetailShowRef = useRef(isAnnotationLinkDetailShow);

    const [forceRender, setForceRender] = useState(0);
    const [openedKnowledgeBook, setOpenedKnowledgeBook] = useState<IKnowledgeBook>(
        KnowledgeBookStore.getSelectedKnowledgeBook()
    );

    const hasManageContentPermission = useMemo(() => { return ComponentHelpers.hasManageContentPermission(); }, []);
    const hasModifyKBPermission = useMemo(
        () => ComponentHelpers.hasModifyKnowledgeBookPermission(openedKnowledgeBook),
        [openedKnowledgeBook]
    );

    const forceUpdate = () => {
        setForceRender(prev => prev + 1); // Increment state to trigger re-render
    };

    const isLoading = () => {
        return isSceneLoading.current;
    }

    const isDirty = () => {
        return isSceneDirty.current;
    }

    const isEditable = () => {
        return isSceneEditable.current;
    }

    function getSiteMap() {
        const ontology = FacilityStore.getSelectedOntology() ?? FacilityStore.getRootOntologyPart();
        return ontology?.Map;
    }

    const updateFacilityState = () => {
        const facility = FacilityStore.getSelectedFacility();

        if (facility) {
            if (ViewerStore.isPanoViewerVisible()) {
                setSiteMap(getSiteMap());
                setViews(facility.Views);
                setAssetTags(facility.AssetTags);
                setAssemblyTemplates(facility.AssemblyTemplates);
                setGraphicSources(FacilityStore.getGraphicSources());
            }
        }
    };
    const calculateMeasurementDistance = (m: Measurement) => {

    };
    const updateViewerState = () => {
        if (ViewerStore.isPanoViewerVisible()) {

            setSelectedView(prevState => {
                const newState = getPanoViewInfo();
                const viewChanged = getViewRenderSignature(newState.view) != getViewRenderSignature(prevState.view);
                return (viewChanged || newState.kvID != prevState.kvID || newState.kvVersion != prevState.kvVersion) ? newState : prevState;
            });

            setKvTagsInfo(prevState => {
                const newState = getKVTagsInfo();
                return (newState.version != prevState.version) ? newState : prevState;
            });

            setKvName(prevName => {
                const newName = ViewerStore.getSelectedKnowledgeView()?.Name;
                return (newName != prevName) ? newName : prevName;
            });

            setKvAssetSetsSignature(previousSignature => {
                const nextSignature = getKVAssetSetsSignature();
                return nextSignature === previousSignature ? previousSignature : nextSignature;
            });

            setSelectedAssembly(ViewerStore.getSelectedAssembly());
            setMeasurements(ViewerStore.getMeasurements());
            setSelectedAnnotation(ViewerStore.getSelectedAnnotation());
            setDisplayObstructedTags(ViewerStore.getDisplayObstructedTags());
            setFocusedAnnotation(ViewerStore.getFocusedAnnotation());
            setFocusedMeasurement(ViewerStore.getFocusedMeasurement());
            setFilteredAssets(ViewerStore.getFilteredAssets());
            setVisibleAssetTagClasses(ViewerStore.getVisibleAssetTagClasses());
            setOccludedAnnotations(ViewerStore.getOccludedAnnotations());
            setDisplayMode(ViewerStore.getDisplayMode());
            setMeasurementType(ViewerStore.getMeasurementType());
            setAssemblyCreationID(ViewerStore.getAssemblyCreationID());
            setAssemblyCreationTemplate(ViewerStore.getAssemblyCreationTemplate());
            const settings = ViewerStore.getSettings();
            setDisplayCompass(settings.PanoCompassVisible);
            setPanoScanSphereColor(settings.PanoScanSphereColor);
            setPanoImageSphereColor(settings.PanoImageSphereColor);
            setTagVolumeBoxColor(settings.TagVolumeBoxColor);
            setMeasurementColor(settings.MeasurementColor);
            setDepthMapLoaded(ViewerStore.isDepthMapLoaded());
            isOpenPointCloud(ViewerStore.isOpenPointCloud());
            setAssemblies(ViewerStore.getAssemblies());
        }
    };

    const isInKnowledgeViewMode = () => {
        return (selectedView?.kvID) ? true : false;
    }

    const updateAppTitle = () => {
        const headerBarTitleElement = document.getElementById("nav-app-title");
        if (headerBarTitleElement) {
            let title: string = isInKnowledgeViewMode() ? kvName : selectedView.view?.Name ?? "";
            if (isEditable()) {
                title += " (edit mode)"
            }

            headerBarTitleElement.textContent = title;
        }
    }

    const resetAppTitle = () => {
        const headerBarTitleElement = document.getElementById("nav-app-title");
        if (headerBarTitleElement)
            headerBarTitleElement.textContent = "";
    }

    const navigateToView = async (view: IView360Detail) => {

        // Do not allow navigation into point viewer for new view if it 
        // does not have point information.
        if (view.ScanFileData == null) {
            SystemActions.displayInfoMessage({
                message: InovxLoader.t('facility:message.cancelNavigationNoPoints'),
            });

            return;
        }

        // Prevent navigation if there are unsaved knowledge view changes
        if (ViewerActions.checkForKnowledgeViewChanges()) return;

        //if selected view and clicked view are different
        const oldViewImage = ViewerStore.getSelectedViewImage();
        if (view.ID !== oldViewImage.ID) {          
            ViewerActions.showPanoViewerImage(view, null, false);
        }
    }

    const updateViewerCamera = (viewer: PointViewer) => {
        if (selectedView.cameraDirection) {
            pointSceneManagerRef.current?.alignToTarget(selectedView.cameraDirection);
        }
        else if (selectedView.cameraProperties) {
            viewer.setCameraProperties(selectedView.cameraProperties.alpha, selectedView.cameraProperties.beta, selectedView.cameraProperties.fov);
        }
    }

    const setCameraAsDirty = () => {
        if (isEditable()) {
            ViewerActions.setCameraAsDirty();
            forceUpdate();
        }
    }
    const isCameraDirty = () => {
        return ViewerStore.isCameraDirty()
    }

    const isAnnotationWithDetail = (annotation: IViewAnnotationTag) => {
        return annotation.AnnotationType == AnnotationType.InformationAnnotation
            || annotation.AnnotationType == AnnotationType.LinkAnnotation || annotation.AnnotationType == AnnotationType.ObservationTag;
    }

    // Viewer initialization
    useEffect(() => {

        const handleFacilityStoreChange = () => {
            updateFacilityState();
        };


        const handleViewerStoreChange = () => {
            updateViewerState();
        };

        const handleKnowledgeBookStoreChange = () => {
            setOpenedKnowledgeBook(KnowledgeBookStore.getSelectedKnowledgeBook());
        };

        const handleFocus = (data: any) => {
            
        }

        const checkForUnsavedChanges = (event: any) => {
            if (isDirty()) {
                event.returnValue = InovxLoader.t("common:msgBox.browserSaveChanges");
            };
        }

        const viewClickedEventHandler = (e: CustomEvent) => {
            // The user clicked on a sphere in the point viewer
            navigateToView(e.detail.view);
        }
        const viewHighlightedEventHandler = (e: CustomEvent) => {
            // The user hovered over a sphere in the point viewer
            setHighlightedView(e.detail.view);
        }

        const labelSelectEventHandler = (e: CustomEvent) => {
            if (!ViewerStore.getAnnotationEditorMode()) {
                if (e.detail.annotationID) {
                    setLastAnnotationPosition({ x: Math.round(e.detail.position.x), y: Math.round(e.detail.position.y) });
                }
                else {
                    setLastAnnotationPosition({ x: 0, y: 0 });
                }
                ViewerActions.selectAnnotation(e.detail.annotationID);
            }
        }
        const annUpdateEventHandler = (e: CustomEvent) => {
            ViewerActions.updateAnnotationProperties((e.detail as any).annotation);
        }

        let degreesChangeTimeout: NodeJS.Timeout = null;

        const updateViewerProperties = (viewer: PointViewer) => {
            if (!degreesChangeTimeout) {
                degreesChangeTimeout = setTimeout(() => {
                    setDirectionDegrees(viewer.cameraDirectionDegrees);
                    ViewerActions.setCameraProperties(viewer.cameraAlpha, viewer.cameraBeta, viewer.cameraFov);
                    setCameraAsDirty();
                    degreesChangeTimeout = null;
                    if (!ViewerStore.getAnnotationEditorMode()) {
                        if (isAnnotationDetailShowRef.current || isAnnotationLinkDetailShowRef.current) {
                            if (isAnnotationDetailShowRef.current) setIsAnnotationDetailShow(false)
                            if (isAnnotationLinkDetailShowRef.current) setIsAnnotationLinkDetailShow(false)
                            ViewerActions.resetAnnotation();
                        }
                    }
                }, 20); // 20ms delay to avoid too many updates
            }
        }

        FacilityStore.addChangeListener(handleFacilityStoreChange);
        ViewerStore.addChangeListener(handleViewerStoreChange);
        KnowledgeBookStore.addChangeListener(handleKnowledgeBookStoreChange);

        window.addEventListener('beforeunload', checkForUnsavedChanges);
        
        const scene = new PointSceneManager(containerRef.current, selectedView.view, views, {
            showTripods: !isInKnowledgeViewMode(),
            scanSphereColor: panoScanSphereColor,
            imageSphereColor: panoImageSphereColor,
            tagVolumeColor: tagVolumeBoxColor,
            measurementColor: measurementColor
        });
        scene.events.addEventListener(PointEvents.Viewer.VIEW_CLICKED, viewClickedEventHandler);
        scene.events.addEventListener(PointEvents.Viewer.VIEW_HIGHLIGHTED, viewHighlightedEventHandler);
        scene.events.addEventListener(PointEvents.Viewer.LABEL_SELECTION, labelSelectEventHandler);
        scene.events.addEventListener(PointEvents.Viewer.ANNOTATION_UPDATE, annUpdateEventHandler);

        pointSceneManagerRef.current = scene;

        const initialAnnotations = selectedView.kvTags ?? [];
        scene.init(initialAnnotations, ViewerStore.getCameraDirection() as any, viewer =>
        {

            viewer.toggleCompass(displayCompass);

            viewer.events.addEventListener(PointEvents.Viewer.VIEWER_DIRECTION_CHANGED, () => {
                updateKbDescriptionPopup(viewer)
                updateViewerProperties(viewer);
            });
            viewer.events.addEventListener(PointEvents.Viewer.VIEWER_FOV_CHANGED, () => {
                updateViewerProperties(viewer);
            });

            //update viewer properties for first load
            updateViewerProperties(viewer);

            updateViewerCamera(viewer);
            isSceneLoading.current = false;
            isSceneDirty.current = false;

            // Setting the active view will display the minimap
            setActiveView(selectedView.view);

            setCameraAsDirty();
        });

        return () => {
            resetAppTitle(); // reset page title

            FacilityStore.removeChangeListener(handleFacilityStoreChange);
            ViewerStore.removeChangeListener(handleViewerStoreChange);
            KnowledgeBookStore.removeChangeListener(handleKnowledgeBookStoreChange);
            window.removeEventListener('beforeunload', checkForUnsavedChanges);
            pointSceneManagerRef.current?.events.removeEventListener(PointEvents.Viewer.VIEW_CLICKED, viewClickedEventHandler);
            pointSceneManagerRef.current?.events.removeEventListener(PointEvents.Viewer.VIEW_HIGHLIGHTED, viewHighlightedEventHandler);
            pointSceneManagerRef.current?.events.removeEventListener(PointEvents.Viewer.LABEL_SELECTION, labelSelectEventHandler);
            pointSceneManagerRef.current?.events.removeEventListener(PointEvents.Viewer.ANNOTATION_UPDATE, annUpdateEventHandler);

            pointSceneManagerRef.current?.dispose();
        };
    }, []);

    const updateKbDescriptionPopup = (viewer: PointViewer) => {
        const { yaw, pitch, fov } = ViewerStore.getViewerCameraProperties();
        const { cameraAlpha, cameraBeta, cameraFov } = viewer;
        if (yaw !== 0 || pitch !== 0 || fov !== 0) {
            if (yaw !== cameraAlpha || pitch !== cameraBeta || fov !== cameraFov) {
                if (KnowledgeBookStore.getShowKnowledgeBookDescription()) KnowledgeBookActions.showKnowledgeBookDescription(false);
            }
        }
    }

    useEffect(() => {
        if (!openPointCloud)
            ViewerActions.updateViewerDirection(2, pointSceneManagerRef.current?.getViewerDirection());
    }, [openPointCloud]);

    // Update pointSceneManager with new measurements whenever measurements state changes
    useEffect(() => {
        const fetchMeasurements = async () => {
            if (selectedView.view) {
                let measurements = await FacilityActions.getView360Measurements(selectedView.view.ID);
                for (var x = 0; x < measurements.length; x++) {
                    var measurement = measurements[x];
                    
                    var distance;

                    var startPosition = new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z);
                    var endPosition = measurement.Type == MeasurementType.Point ? null : new BABYLON.Vector3((measurement as any).Coordinates[1].X, (measurement as any).Coordinates[1].Y, (measurement as any).Coordinates[1].Z);

                    if (measurement.Type == MeasurementType.Distance) {

                        distance = (endPosition.subtract(startPosition).length());
                    }
                    else if (measurement.Type == MeasurementType.XOrtho) {

                        var pB = new BABYLON.Vector3(endPosition.x, startPosition.y, startPosition.z);
                        var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                        distance = (pB.subtract(pA).length());
                    }
                    else if (measurement.Type == MeasurementType.YOrtho) {

                        var pB = new BABYLON.Vector3(startPosition.x, endPosition.y, startPosition.z);
                        var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                        distance = (pB.subtract(pA).length());
                    }
                    else if (measurement.Type == MeasurementType.ZOrtho) {

                        var pB = new BABYLON.Vector3(startPosition.x, startPosition.y, endPosition.z);
                        var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                        distance = (pB.subtract(pA).length());
                    }

                    //var unitInfo = ViewerStore.getUnitInformation();
                    var jsonMeasurement = measurement.Type == MeasurementType.Point
                        ? { checked: false, name: measurement.Name, versionToken: measurement.VersionToken, saved: true, id: measurement.ID, measureMode: measurement.Type, startPosition: new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z) }
                        : { checked: false, name: measurement.Name, versionToken: measurement.VersionToken, saved: true, measureMode: measurement.Type, startPosition: new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z), endPosition: new BABYLON.Vector3((measurement as any).Coordinates[1].X, (measurement as any).Coordinates[1].Y, (measurement as any).Coordinates[1].Z), distance: distance, id: measurement.ID };

                    ViewerActions.createMeasurement(jsonMeasurement as any);
                }
            }
        };

        if (depthMapLoaded) {
            fetchMeasurements();
        }
    }, [depthMapLoaded]);

    // Update pointSceneManagerRef with new measurements whenever measurements state changes
    useEffect(() => {
        pointSceneManagerRef.current?.setMeasurements(measurements);
    }, [measurements]);

    useEffect(() => {
        pointSceneManagerRef.current?.setAssemblies(assemblies);
    }, [assemblies]);

    useEffect(() => {
        pointSceneManagerRef.current?.setGraphicSources(graphicSources);
    }, [graphicSources]);

    useEffect(() => {
        pointSceneManagerRef.current?.selectAssembly(selectedAssembly);
    }, [selectedAssembly]);
    // Update the pano scene when the active view or KV changes
    useEffect(() => {
        if (isLoading())
            return;

        if (selectedView.view != activeView) {
            isSceneLoading.current = true;
            isSceneDirty.current = false;

            // Initiate the view change sequence. This will do everything from loading
            // the new image to loading and updating the depth map.
            const showTripods = !isInKnowledgeViewMode();
            const cameraProperties = isInKnowledgeViewMode() ? selectedView.cameraProperties : null;

            const initialAnnotations = selectedView.kvTags ?? [];
            
            pointSceneManagerRef.current?.changeSelectedView(selectedView.view, initialAnnotations, assemblyTemplates, assemblies, graphicSources, cameraProperties, showTripods, () => {
                updateViewerCamera(pointSceneManagerRef.current.viewer);
                isSceneLoading.current = false;
                isSceneDirty.current = false;

                // The view change is complete. Depth map still needs to be loaded, but
                // at this point we can update the active view (and thus the minimap).
                setActiveView(selectedView.view);
            }).then(() => {
                loadAnnotations();
                loadAssemblies();
                loadGraphicSources();
            });
        }
        else {
            isSceneLoading.current = true;

            const showTripods = !isInKnowledgeViewMode();
            pointSceneManagerRef.current?.setTripodsDisplay(showTripods);
            updateViewerCamera(pointSceneManagerRef.current.viewer);

            isSceneLoading.current = false;
            isSceneDirty.current = false;
        }
    }, [selectedView]);

    useEffect(() => {
        isSceneEditable.current = hasModifyKBPermission && isInKnowledgeViewMode() && isEditing;
        if (!isEditable() && isDirty()) {
            isSceneDirty.current = false;
            forceUpdate();
        }
        if (isEditing) ViewerActions.resetDisplayMode();
    }, [hasModifyKBPermission, isEditing]);

    // Re-align the camera to a selected annotation
    useEffect(() => {
        if (isLoading())
            return;

        if (focusedAnnotation?.Target3D) {
            pointSceneManagerRef.current?.alignToTarget(focusedAnnotation.Target3D);
        }
    }, [focusedAnnotation]);

    useEffect(() => {
        if (isLoading())
            return;

        if (focusedMeasurement != null) {

            var point: IVector3D;
            var centerPoint: any;
            if (focusedMeasurement.measureMode == MeasurementType.Point) {
                point = { X: (focusedMeasurement as any).startPosition.x, Y: (focusedMeasurement as any).startPosition.y, Z: (focusedMeasurement as any).startPosition.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.Distance) {

                centerPoint = (focusedMeasurement as any).startPosition.add((focusedMeasurement as any).endPosition.subtract((focusedMeasurement as any).startPosition).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.XOrtho) {

                var pB = new BABYLON.Vector3((focusedMeasurement as any).endPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);
                var pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);

                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.YOrtho) {

                var pB = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).endPosition.y, (focusedMeasurement as any).startPosition.z);
                var pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);

                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.ZOrtho) {

                var pB = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).endPosition.z);
                var pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);

                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }

            pointSceneManagerRef.current?.alignToTarget(point);
        }
    }, [focusedMeasurement]);

    useEffect(() => {
        const onFacilityAssetSetsChange = () => setFacilityAssetSetsRevision(revision => revision + 1);
        FacilityAssetSetStore.addChangeListener(onFacilityAssetSetsChange);
        return () => FacilityAssetSetStore.removeChangeListener(onFacilityAssetSetsChange);
    }, []);

    useEffect(() => {
        loadAnnotations();
    }, [assetTags, kvTagsInfo, kvAssetSetsSignature, facilityAssetSetsRevision, selectedAnnotation, occludedAnnotations, displayObstructedTags, filteredAssets, visibleAssetTagClasses, isEditing]);

    useEffect(() => {
        const id = setTimeout(() => {
            updateAppTitle();
        }, 300); // Delay to ensure state updates are settled

        return () => {
            clearTimeout(id);
        }
    }, [selectedView, kvName, isEditing]);

    useEffect(() => {
        isAnnotationLinkDetailShowRef.current = isAnnotationLinkDetailShow;
        isAnnotationDetailShowRef.current = isAnnotationDetailShow;
        isAnnotationTextShowRef.current = isAnnotationTextShow;
    }, [isAnnotationLinkDetailShow, isAnnotationDetailShow, isAnnotationTextShow ]);

    useEffect(() => {
        if (selectedAnnotation) {
            const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
            if (selectedKVTag && isAnnotationWithDetail(selectedKVTag)) {
                if (selectedKVTag.AnnotationType === AnnotationType.InformationAnnotation) {
                    //show the popup with the annotation detail (rich text control)
                    setIsAnnotationLinkDetailShow(false)
                    setIsAnnotationDetailShow(true)
                    setIsAnnotationTextShow(false)
                } else if (selectedKVTag.AnnotationType === AnnotationType.LinkAnnotation) {
                    setIsAnnotationDetailShow(false);
                    setIsAnnotationLinkDetailShow(true)
                    setIsAnnotationTextShow(false)
                } else if (selectedKVTag.AnnotationType === AnnotationType.ObservationTag) {
                    setIsAnnotationDetailShow(false);
                    setIsAnnotationLinkDetailShow(false)
                }
            }
            else {
                if (!ViewerStore.getAnnotationEditorMode()) {
                    //hide any existing annotation detail popup
                    setIsAnnotationDetailShow(false)
                    setIsAnnotationLinkDetailShow(false)
                    setIsAnnotationTextShow(false)
                }
            }
        } else {
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationDetailShow(false);
            setIsAnnotationTextShow(false)
            resetAnnotationEditorMode();
        }
    }, [selectedAnnotation]);

    const resetAnnotationEditorMode = () => {
        setTimeout(() => {
            ViewerActions.setAnnotationEditorMode(false)
        }, 1)
    }
    useEffect(() => {
        pointSceneManagerRef.current?.viewer?.setMeasurementType(measurementType);
    }, [measurementType]);
    useEffect(() => {
        pointSceneManagerRef.current?.viewer?.setAssemblyCreationID(assemblyCreationID);
    }, [assemblyCreationID]);
    useEffect(() => {
        pointSceneManagerRef.current?.viewer?.setAssemblyCreationTemplate(assemblyCreationTemplate);
    }, [assemblyCreationTemplate]);
    useEffect(() => {
        const pointClickedEventHandler = async (e: CustomEvent) => {
            const point3D: IVector3D = e.detail.point3D;
            const point2D: IVector2D = e.detail.point2D;
            const cameraPosition: IVector3D = e.detail.cameraPosition;

            setLastAnnotationPosition({ x: point2D.X, y: point2D.Y });

            if (displayMode === DisplayMode.CreateAnnotation) {
                const annotationType = ViewerStore.getAnnotationType();
                if (annotationType === AnnotationType.AssetTag || annotationType === AnnotationType.AssetTagWithBox) {
                    await ViewerActions.createTempAssetTag(point3D, cameraPosition);
                } else {
                    const annotationID = await ViewerActions.createAnnotation(point3D);
                    ViewerActions.selectAnnotation(annotationID);

                    ViewerActions.setAnnotationEditorMode(true);
                    if (annotationType === AnnotationType.ObservationTag) {
                        setIsAnnotationTextShow(true);
                    }
                }
            }
            else {
                const tag = assetTags.find(tag => tag.ID == e.detail.annotationID);
                await ViewerActions.repositionAssetTag(tag, point3D, cameraPosition);
            }

            ViewerActions.resetDisplayMode();
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                ViewerActions.undoAssembly();
                ViewerActions.resetDisplayMode();
            }
        };
        
        pointSceneManagerRef.current?.viewer?.setDisplayMode(displayMode);

        if (assemblyCreationTemplate !== undefined || measurementType !== null || displayMode === DisplayMode.CreateAnnotation || displayMode === DisplayMode.PositionAnnotation || displayMode === DisplayMode.AssemblyVolumeMove || displayMode === DisplayMode.AssemblyVolumeScale) {
            pointSceneManagerRef.current?.events.addEventListener(PointEvents.Viewer.POINT3D_CLICKED, pointClickedEventHandler);
            window.addEventListener('keydown', handleKeyDown);
        }
        if (displayMode === DisplayMode.Measurement) {
            if (isEditing) {
                setIsEditing(false);
                resetAnnotationEditorMode();
            }

            if (isAnnotationTextShow) {
                setIsAnnotationTextShow(false)
            }
            if (speedDialRef?.current) {
                speedDialRef.current.hide();
                setShouldSpeedDialClose(true)
            }
        }

        return () => {
            if (assemblyCreationTemplate !== null || measurementType !== undefined || displayMode === DisplayMode.CreateAnnotation || displayMode === DisplayMode.PositionAnnotation || displayMode === DisplayMode.AssemblyVolumeMove || displayMode === DisplayMode.AssemblyVolumeScale) {
                pointSceneManagerRef.current?.events.removeEventListener(PointEvents.Viewer.POINT3D_CLICKED, pointClickedEventHandler);
                window.removeEventListener('keydown', handleKeyDown);
            }
        };

    }, [displayMode, measurementType, assemblyCreationTemplate]);

    useEffect(() => {
        pointSceneManagerRef.current?.viewer?.toggleCompass(displayCompass);
    }, [displayCompass]);

    useEffect(() => {
        pointSceneManagerRef.current?.changeScanSphereColor(panoScanSphereColor);
    }, [panoScanSphereColor]);

    useEffect(() => {
        pointSceneManagerRef.current?.changeImageSphereColor(panoImageSphereColor);
    }, [panoImageSphereColor]);

    useEffect(() => {
        pointSceneManagerRef.current?.changeTagVolumeColor(tagVolumeBoxColor);
    }, [tagVolumeBoxColor]);

    useEffect(() => {
        pointSceneManagerRef.current?.viewer?.changeMeasurementColor(measurementColor);
    }, [measurementColor]);

    const onMapViewHighlighted = (view: IView360Detail) => {
        // No higlighting if the scene is busy (i.e. changing views)
        if (!pointSceneManagerRef.current?.isBusy) {
            if (view) {
                pointSceneManagerRef.current?.highlightView(view.ID);
            }
            else {
                pointSceneManagerRef.current?.clearHighlightedView();
            }
        }
    }
    const loadGraphicSources = () => {
        GraphicSourceActions.getGraphicSources(FacilityStore.getSelectedFacility().ID);
    }
    const loadAssemblies = () => {
        ViewerActions.createAssemblyInstances(FacilityStore.getSelectedFacility().Assemblies);
    }
    const loadAnnotations = () => {
        let allDisplayedTags: IAnnotationDisplayItem[] = (kvTagsInfo.tags) ? kvTagsInfo.tags : [];

        let displayedAssetTags = ViewerHelpers.filterAssetTagsByVisibleClasses(
            assetTags.filter(tag => !filteredAssets.includes(tag.AssetID)),
            visibleAssetTagClasses,
            FacilityStore.getSelectedFacility()
        );

        const selectedKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        if (selectedKnowledgeView) {
            const resolution = ViewerHelpers.resolveKnowledgeViewAssetTags(
                selectedKnowledgeView.AssetSets,
                mergeViewerAssetSets(
                    KnowledgeBookStore.getAllAssetSet(),
                    FacilityAssetSetStore.getAssetSets(FacilityStore.getSelectedFacility()?.ID)
                )
            );
            const visibleKnowledgeViewTagIDs = new Set(resolution.visibleTagIDs);
            const claimedTagIDs = new Set([...resolution.visibleTagIDs, ...resolution.hiddenTagIDs]);
            displayedAssetTags = displayedAssetTags.filter(tag =>
                visibleKnowledgeViewTagIDs.has(tag.ID) || (resolution.fallbackVisible && !claimedTagIDs.has(tag.ID)));
        }
        allDisplayedTags = allDisplayedTags.concat(displayedAssetTags);
        
        if (!displayObstructedTags) {
            allDisplayedTags = allDisplayedTags.filter(tag => !occludedAnnotations.some(otID => otID == tag.ID));
        }

        const annotationObj = allDisplayedTags.find(tag => tag.ID == selectedAnnotation);
        pointSceneManagerRef.current?.setAnnotations(allDisplayedTags, annotationObj, !isEditable(), displayObstructedTags);
    }

    const onViewerSave = async () => {
        // This will reset the internal editing/dirty flags through the isEditing useEffect handler.
        await ViewerActions.saveKnowledgeViewChanges();
    }

    const onCameraSave = async () => {
        await onViewerSave();
        ViewerActions.resetCameraAsDirty()
    }

    const checkTextAnnotationDisable = () => {
        if (isEditing && selectedAnnotation) {
            const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
            if (selectedKVTag && isAnnotationWithDetail(selectedKVTag)) {
                if (selectedKVTag.AnnotationType === AnnotationType.InformationAnnotation) {
                    return false;
                } else if (selectedKVTag.AnnotationType === AnnotationType.LinkAnnotation) {
                    return false;
                } else if (selectedKVTag.AnnotationType === AnnotationType.ObservationTag) {
                    return false;
                }
            }
        }
        return true;
    }

    const openAnnotation = () => {
        const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
        if (selectedKVTag && !ViewerStore.getAnnotationEditorMode()) {
            ViewerActions.setAnnotationEditorMode(true)
        }
        if (selectedKVTag.AnnotationType === AnnotationType.InformationAnnotation) {
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationTextShow(false)
            setIsAnnotationDetailShow(true);
        } else if (selectedKVTag.AnnotationType === AnnotationType.LinkAnnotation) {
            setIsAnnotationDetailShow(false);
            setIsAnnotationTextShow(false)
            setIsAnnotationLinkDetailShow(true)
        } else if (selectedKVTag.AnnotationType === AnnotationType.ObservationTag) {
            setIsAnnotationDetailShow(false);
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationTextShow(true)
        }
    }

    const speedDialMenus: SpeedDialItemModel[] = [
        { id: "information-annotation", iconCss: 'information-annotation', title: InovxLoader.t("facility:action.informationAnnotation"), disabled: isEditing ? false : true },
        { id: "link-annotation", iconCss: 'link-annotation', title: InovxLoader.t("facility:action.linkAnnotation"), disabled: isEditing ? false : true },
        { id: "text-annotation", iconCss: 'text-annotation', title: InovxLoader.t("facility:action.textAnnotation"), disabled: isEditing ? false : true },
        { id: "save-camera", iconCss: 'save-camera', title: InovxLoader.t("facility:action.saveCamera"), disabled: isInKnowledgeViewMode() && isCameraDirty() ? false : true },
        { id: "edit-text-annotation", iconCss: 'edit-text-annotation', title: InovxLoader.t("facility:action.editTextAnnotation"), disabled: checkTextAnnotationDisable() },
        { id: "delete-annotation", iconCss: 'delete-annotation', title: InovxLoader.t("facility:action.deleteAnnotation"), disabled: checkTextAnnotationDisable() },
    ];

    const speedDialRef = useRef(null); 
    function annotationActionTemplate(props: any): JSX.Element {
        let classname: string = "s-icons " + props.properties.iconCss;
        return (
            <div className="s-icon-container">
                <span title={props.properties.title} className={classname}></span>
            </div>);
    }

    const actionItemClick = (args: SpeedDialItemEventArgs) => {
        if (args.name === 'clicked') {
            if (args.item.id === 'information-annotation') {
                switchToCreateAnnotationMode(AnnotationType.InformationAnnotation)
            } else if (args.item.id === 'link-annotation') {
                switchToCreateAnnotationMode(AnnotationType.LinkAnnotation)
            } else if (args.item.id === 'text-annotation') {
                switchToCreateAnnotationMode(AnnotationType.ObservationTag)
            } else if (args.item.id === 'save-camera') {
                onCameraSave();
            } else if (args.item.id === 'delete-annotation') {
                deleteAnnotation()
            } else if (args.item.id === 'edit-text-annotation') {
                openAnnotation();
            }
        }
    }
    const handleBeforeOpen = (args: any) => {
        if (args.name === 'beforeOpen') {
            if (!kvName) return;
            setIsEditing(true)
            setSpeedDialButtonTitle(InovxLoader.t("facility:action.close"))
        }
    }

    const handleBeforeClose = async (args: any) => {
        if (args.name === 'beforeClose') {
            if (args?.event?.currentTarget?.id === 'annotation-actions') {
                if (isEditing) {
                    args.cancel = true;
                    const selectedKV= await ViewerStore.getSelectedKnowledgeView();
                    if (selectedKV) {
                        setIsEditing(false)
                        if (isAnnotationTextShow) {
                            setIsAnnotationTextShow(false)
                            ViewerActions.resetAnnotation();
                        }
                        resetAnnotationEditorMode();
                        
                        const button = document.getElementById('annotation-actions');
                        button.click();
                    }
                } else {
                    args.cancel = false;
                    setSpeedDialButtonTitle(InovxLoader.t("facility:action.editAnnotation"))
                }
            } else if (!args.event && shouldSpeedDialClose) {
                args.cancel = false;
                setIsEditing(false)
                setSpeedDialButtonTitle(InovxLoader.t("facility:action.editAnnotation"))
                setShouldSpeedDialClose(false)
            } else if (args?.event?.currentTarget?.id === 'save-camera') {
                args.cancel = true;
            } else {
                args.cancel = true;
            }
        }
    }

    const switchToCreateAnnotationMode = (tagType: AnnotationType) => {
        if (!isEditing) return;

        ViewerActions.setCreateAnnotationDisplayMode(tagType);
    }
    const deleteAnnotation = () => {
        const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
        ViewerActions.deleteAnnotation(selectedKVTag);
        ViewerActions.resetAnnotation();
    }

    const displayMiniMap = props.displayMiniMap && activeView;
    const displayText = (displayMode == DisplayMode.CreateAnnotation) ? InovxLoader.t('facility:message.pickTagLocation')
        : (displayMode == DisplayMode.PositionAnnotation) ? InovxLoader.t('facility:message.repositionTag')
            : (measurementType == MeasurementType.Point) ? InovxLoader.t('facility:message.pickPointLocation')
                : (measurementType != null) ? InovxLoader.t('facility:message.pickDistanceLocation')
                    : (assemblyCreationTemplate != null) ? InovxLoader.t('facility:message.pickAssemblyLocation')
                        :   null;

    const animation: SpeedDialAnimationSettingsModel = { effect: 'None' };
    var buttonClass = ViewerStore.getSelectedViewImage()?.ScanFileData != null ? "mr-30" : "mr-18";
    return (
        <>
            {kvName && document.getElementById('PanoContainer') && hasManageContentPermission && <SpeedDialComponent
                ref={speedDialRef}
                target="#PanoContainer"
                id='annotation-actions'
                openIconCss='e-icons e-edit-white'
                closeIconCss='e-icons e-close'
                items={speedDialMenus}
                itemTemplate={annotationActionTemplate}
                clicked={actionItemClick}
                beforeOpen={handleBeforeOpen}
                beforeClose={handleBeforeClose}
                position="BottomRight"
                mode='Linear'
                title={speedDialButtonTitle}
                enablePersistence={true}
                animation={animation}
                cssClass={buttonClass}
                direction='Up'>
            </SpeedDialComponent>}

            {isAnnotationLinkDetailShow && <AnnotationLinkEditor
                onClose={() => setIsAnnotationLinkDetailShow(false)}
                left={lastAnnotationPosition.x}
                top={lastAnnotationPosition.y}
            />}
            {isAnnotationDetailShow && <AnnotationTextEditor
                onClose={() => setIsAnnotationDetailShow(false)}
                left={lastAnnotationPosition.x}
                top={lastAnnotationPosition.y}
            />}
            {isAnnotationTextShow && ViewerStore.getAnnotationEditorMode() && <AnnotationEditor
                onClose={() => setIsAnnotationTextShow(false)}
                left={lastAnnotationPosition.x}
                top={lastAnnotationPosition.y}
            />}
            <div id="pano-viewer" className="pano-viewer-container minimap-container" ref={containerRef}>

                {(displayText) && (
                    <div className="overlay-text">{displayText}</div>
                )}
                {displayMiniMap && (
                    <FacilityMiniMap
                        siteMap={siteMap}
                        views={views}
                        title={facilityTitle}
                        targetId="#pano-viewer"
                        activeView={activeView}
                        highlightedView={highlightedView}
                        viewerDirectionDegrees={directionDegrees}
                        knowledgeViewOpened={ViewerStore.getSelectedKnowledgeView() != null}
                        pointCloudAvailable={ViewerStore.getSelectedViewImage().ScanFileData != null }
                        onViewMarkerClicked={navigateToView}
                        onViewMarkerHighlighted={(view, _, highlighted) => { onMapViewHighlighted(highlighted ? view : null); }}
                    />
                )}
            </div>
        </>
    );
});

export default ReactPointViewer;
